import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DataSearchResponseItem, DataSearchResponseItemVersion } from '../../../services/endpoints/data'
import './DataViewCell.css'
import ExtractionNotesDialog from '../Modals/ExtractionNotesDialog/ExtractionNotesDialog'
import ScriptDialog from '../Modals/ScriptsDialog/ScriptsDialog'
import DataSpyFileSelectDialog from '../Modals/DataSpyFileSelectDialog/DataSpyFileSelectDialog'
import TextDialog from '../Modals/TextDialog/TextDialog'
import HtmlDialog from '../Modals/HtmlDialog/HtmlDialog'
import MediaDialog from '../Modals/MediaDialog/MediaDialog'
import LocationMapWrapper from '../Modals/LocationsModal/LocationsWrapper'
interface ResultsGridViewColProps {
    key: DataSearchResponseItem
}
export const ResultsGridViewCol: React.FC<ResultsGridViewColProps> = ({key}) => {
    //console.log('--------------------- ', key)
   
    const dsItems = key.versions
    .filter(i => key.dataSpy.indexOf(i.extension) !== -1)

    const vItems = key.versions
    .filter(i => key.viewable.indexOf(i.extension) !== -1)
    .reduce<{[k: string]: DataSearchResponseItemVersion}>((a, b) => {
        a[b.extension] = b

        return a
    }, {})
        
    const dataSpyEnabled = true //todo need to pull settings to see if it's available
    
    return (
      <div className="view-btns">
        <_AdditionalItemsDropdown
          uploadId={key.uploadId}
          script={key.script}
          hasNotes={key.extractionNotesTxt}
          vehicleName={key.vehicle.displayName}
          request={{
            vehicleId: key.vehicle.id,
            startDate: key.startDate,
            endDate: key.endDate
          }}
        />
        {dataSpyEnabled && dsItems.length > 0 && (
          <_DataSpyViewer items={dsItems} />
        )}
        <_AllViewer fileName={key.displayName} items={vItems} fileId={key.id} />
      </div>
    )
}

interface _ItemsDropdownPropsItem {
    title: string
    className: string
    onClick: (e: MouseEvent) => void
}
interface _ItemsDropdownProps {
    className: string
    title: string
    items: _ItemsDropdownPropsItem[]
}
export const _ItemsDropdown: React.FC<_ItemsDropdownProps> = ({
    className,
    title,
    items
}) => {
    const [open, setOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    const handleClickOutside = (e: Event) => {
        if (
            dropdownRef.current &&
            e.target instanceof Node &&
            !dropdownRef.current.contains(e.target)
        ) {
            setOpen(false)
        }
    }

    //SEV - Not sure I'm happy with this
    useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            document.removeEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [open])

    const handleOnItemClick = (e: MouseEvent<HTMLLIElement>, cb: (e: MouseEvent<HTMLLIElement>) => void) => {
        setOpen(false)
        cb(e)
    }

    return (
        <>
            <div className="custom-dropdown" ref={dropdownRef}>
                <a className="dropdown-trigger" onClick={() => setOpen(!open)}>
                    <span className={`glyphicons ${className}`}></span>
                    <i className="dx-icon dx-icon-spindown"></i>
                    <span className="custom-tooltip">{title}</span>
                </a>
                <ul className={`dropdown-list ${open ? 'active' : ''}`}>
                  {items.map((i, index)=> {
                    return (
                      <li
                        key={`${i.title}-${index}`}
                        className="dropdown-item"
                        onClick={(e) => handleOnItemClick(e, i.onClick)}
                      >
                        <span className={`glyphicons ${i.className}`}></span>
                        <span className="dropdown-text">{i.title}</span>
                      </li>
                    ) 
                  })}
                </ul>
            </div>
        </>
    )
}
  
type _AdditionalItemsDropdownDialogs = 'extractionNotes' | 'locations' | 'script'
interface _AdditionalItemsDropdownProps {
    uploadId: number
    script: {id: number, name: string} | null
    hasNotes: boolean
    vehicleName: string
    request: {
        vehicleId: number
        startDate: string
        endDate: string
    }
}

export const _AdditionalItemsDropdown: React.FC<_AdditionalItemsDropdownProps> = ({
    uploadId,
    script,
    hasNotes,
    vehicleName,
    request
}) => {
    const [openDialog, setOpenDialog] = useState<_AdditionalItemsDropdownDialogs | null>(null) 

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(null)
    }, [openDialog])

    const handleItemOnClick = useCallback((type: _AdditionalItemsDropdownDialogs) => {
        return (_) => {
            setOpenDialog(type)
        }
    }, [setOpenDialog])

    const items: _ItemsDropdownPropsItem[] = []
    if(hasNotes && script) items.push({
        title: 'Extraction Notes',
        className: 'glyphicons-article',
        onClick: handleItemOnClick('extractionNotes')
    })
    if(script) items.push({
        title: `Script: ${script.name}`,
        className: 'glyphicons-notes',
        onClick: handleItemOnClick('script')
    })
    items.push({
      title: 'Locations',
      className: 'glyphicons-map-marker',
      onClick: handleItemOnClick('locations')
    })

    if(items.length < 1) {
        return <></>
    }


    return (
      <>
        {openDialog === 'extractionNotes' && (
          <ExtractionNotesDialog
            onClose={handleCloseDialog}
            uploadId={uploadId}
            script={script!}
          />
        )}
        {openDialog === 'script' && (
          <ScriptDialog onClose={handleCloseDialog} script={script!} />
        )}
        {openDialog === 'locations' && (
          <LocationMapWrapper
            isOpen={true}
            onClose={handleCloseDialog}
            vehicleName={vehicleName}
            request={{
              ...request,
              sort: 'gpsTimestamp',
              order: 'asc',
              offset: 0,
              max: 500
            }}
          />
        )}
        <_ItemsDropdown
          className="glyphicons-package glyphicons-line-height-grid"
          title="Associated Items"
          items={items}
        />
      </>
    )
}

interface _DataSpyViewerProps {
    items: DataSearchResponseItemVersion[]
}
export const _DataSpyViewer: React.FC<_DataSpyViewerProps> = ({items}) => {
    const [showDialog, setShowDialog] = useState(false)

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        if(e.altKey) {
            setShowDialog(true)
            e.preventDefault()
        }
    }

    return (
        <>
            {showDialog && <DataSpyFileSelectDialog onClose={() => setShowDialog(false)} versions={items} />}
            <div className='btn-group'>
                <ul>
                    <li>
                        <a
                        href={`/dataspy/?fileId=${items[0].id}`}
                        className='dataspy-view-btn'
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleClick}
                        >
                        <span className="glyphicons glyphicons-stats"></span>
                        <div className="custom-tooltip">
                            <span className="tooltip-title">View with DataSpy</span>
                            <div className="tooltip-body">
                            <span className="tooltip-text">Click to open or alt-click to select options</span>
                            </div>
                        </div>
                        </a>
                    </li>
                </ul>
            </div>
        </>
    )
}


type _AllViewerDialogs = 'txt' | 'mp3' | 'mp4' | 'html' | 'htm' | 'xml' | 'dm1'
type _AllViewerButtons = {type: string, glyph: string, name: string, link?: string, dialog?: _AllViewerDialogs}
interface _AllViewerProps {
    items: { [key: string]: DataSearchResponseItemVersion }
    fileId: number
    fileName: string
}
export const _AllViewer: React.FC<_AllViewerProps> = ({
    items, fileId, fileName
}) => {
    const [openDialog, setOpenDialog] = useState<_AllViewerDialogs | null>(null)

    const viewButtons: _AllViewerButtons[] = [
        {type: 'Log CSV', glyph: 'table', name: 'Log CSV', link: '/ui/#!/data/quickview/csv/'},
        {type: 'db', glyph: 'database-search', name: 'DB Viewer', link: '/ui/#!/data/quickview/db/'},
        {type: 'htm', glyph: 'global', name: 'HTML Viewer', dialog: 'htm'},
        {type: 'html', glyph: 'global', name: 'HTML Viewer', dialog: 'html'},
        {type: 'mp3', glyph: 'voice', name: 'MP3 Viewer', dialog: 'mp3'},
        {type: 'mp4', glyph: 'facetime-video', name: 'MP4 Viewer', dialog: 'mp4'},
        {type: 'txt', glyph: 'article', name: 'Text Viewer', dialog: 'txt'},
        {type: 'xml', glyph: 'embed', name: 'XML DTC Viewer', link: '/ui/#!/data/quickview/xml/'},
        {type: 'dm1', glyph: 'embed', name: 'XML DM1 Viewer', link: '/ui/#!/data/quickview/dm1/'}
    ]

    const handleClose = () => setOpenDialog(null)

    const getOnclick = (link: string | undefined, dialog: _AllViewerDialogs | undefined) => {
        if(dialog) {
            return (_) => setOpenDialog(dialog)
        } else if(link) {
            return (_) => {
                window.open(link, '_blank', 'noopener,noreferrer');
            }
        } else {
            throw new Error('No valid option handling viewer button click')
        }

    }

    const buttonList = useMemo(() => {
        return viewButtons
        .filter(i => items[i.type] ? true : false)
        .map<_ItemsDropdownPropsItem>(i => {
            const link = (i.link) ? `${i.link}${fileId}` : undefined
            return {
                title: i.name,
                className: `glyphicons-${i.glyph}`,
                onClick: getOnclick(link, i.dialog)
            }
        })
    }, [items])

    const renderButton = (item: _ItemsDropdownPropsItem, index: number) => {
        return (
          <li key={`button-${item.title}-${index}`}>
            <a onClick={item.onClick}>
              <span className={`glyphicons ${item.className}`}></span>
              <span className="custom-tooltip">{item.title}</span>
            </a>
          </li>
        )
    }

    return (
        <>
            {openDialog ==='htm' && <HtmlDialog fileName={fileName} uploadFileVersionId={items['htm'].id} onClose={handleClose} />}
            {openDialog ==='html' && <HtmlDialog fileName={fileName} uploadFileVersionId={items['html'].id} onClose={handleClose} />}
            {openDialog === 'mp3' && <MediaDialog fileName={fileName} uploadFileVersionId={items['mp3'].id} onClose={handleClose} type='mp3' />}
            {openDialog === 'mp4' && <MediaDialog fileName={fileName} uploadFileVersionId={items['mp4'].id} onClose={handleClose} type='mp4' />}
            {openDialog === 'txt' && <TextDialog fileName={fileName} uploadFileVersionId={items['txt'].id} onClose={handleClose} />}

            {buttonList.length < 2 && <div className="btn-group">
                {buttonList.map((i, index) => {
                    return renderButton(i, index)
                })}
            </div>}
            {buttonList.length > 1 && <_ItemsDropdown
                className='glyphicons-more-windows glyphicons-line-height-grid'
                title="Multiple Data Viewers"
                items={buttonList}
            />}
        </>
    )
}
